import { withAuthenticationContext, useSecureGraphqlQuery, TFunction } from '@psyomics/ui-common';
import {
  ActivityStat,
  PageLayout,
  LoadingSpinner,
  TableFilters,
  type ActivityStatIcon,
  type FilterType,
} from '@psyomics/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetOrgStatsDocument,
  GetOrgStatsQuery,
  GetOrgStatsQueryVariables,
  GetOrgStatsResponse,
} from '../referrals/generated/referrals';
import { useOrganisationServiceCodes } from '../referrals/use-organisation-service-codes';

const DEFAULT_DAYS = 30;
const START_OF_DAY = [0, 0, 0, 0] as const;
const END_HOUR = 23;
const END_MINUTE = 59;
const END_SECOND = 59;
const END_MILLIS = 999;
const END_OF_DAY = [END_HOUR, END_MINUTE, END_SECOND, END_MILLIS] as const;

const makeCard = ({
  icon,
  key,
  t,
  value,
}: {
  icon: ActivityStatIcon;
  key: string;
  t: TFunction;
  value: string | number;
}) => ({
  icon,
  title: t(`page.activityDashboard.${key}.title`),
  value: String(value),
});

const buildStats = (
  t: TFunction,
  stats: GetOrgStatsResponse
): Array<{
  icon: ActivityStatIcon;
  title: string;
  value: string;
}> => [
  makeCard({
    icon: 'notepadText',
    key: 'invitesCard',
    t,
    value: stats.inviteCount,
  }),
  makeCard({
    icon: 'plus',
    key: 'accountCreatedCard',
    t,
    value: stats.accountCreatedCount,
  }),
  makeCard({
    icon: 'barChart3',
    key: 'reportsCompletedCard',
    t,
    value: stats.reportsCompletedCount,
  }),
  makeCard({
    icon: 'alarmClockOff',
    key: 'cancelledCard',
    t,
    value: stats.cancelledCount,
  }),
  makeCard({
    icon: 'clock',
    key: 'expiredCard',
    t,
    value: stats.expiredCount,
  }),
  makeCard({
    icon: 'checkCheck',
    key: 'completionRateCard',
    t,
    value: stats.completionRatePercentage == null ? '- %' : `${stats.completionRatePercentage}%`,
  }),
];

const Stats = () => {
  const { t } = useTranslation();
  const { serviceCodes: orgServiceCodes } = useOrganisationServiceCodes();

  const defaultToDate = new Date();
  defaultToDate.setHours(...END_OF_DAY);

  const defaultFromDate = new Date(new Date().setDate(defaultToDate.getDate() - DEFAULT_DAYS));
  defaultFromDate.setHours(...START_OF_DAY);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);

  const [serviceCodes, setServiceCodes] = useState<string[] | null>([]);

  const filters: FilterType[] = [
    {
      id: 'referraldate',
      kind: 'date',
      name: t('page.activityDashboard.referralDateLabel'),
      onChange: {
        from: setFromDate,
        to: setToDate,
      },
      value: {
        from: fromDate,
        to: toDate,
      },
    },
    {
      defaultValue: [],
      id: 'service',
      kind: 'multi-select',
      name: t('model.referrals.service'),
      onChange: setServiceCodes,
      value: serviceCodes,
      values: orgServiceCodes,
    },
  ];

  const [{ data, fetching }] = useSecureGraphqlQuery<GetOrgStatsQuery, GetOrgStatsQueryVariables>({
    query: GetOrgStatsDocument,
    variables: {
      input: {
        fromDate: fromDate ? String(fromDate) : undefined,
        toDate: toDate ? String(toDate) : undefined,
        serviceCodes: serviceCodes ?? undefined,
      },
    },
  });
  const stats = useMemo(() => (data?.getOrgStats ? buildStats(t, data.getOrgStats) : []), [t, data?.getOrgStats]);

  if (fetching) return <LoadingSpinner />;

  return <StatsDisplay filters={filters} stats={stats} />;
};

const StatsDisplay = ({
  filters,
  stats,
}: {
  readonly filters: FilterType[];
  readonly stats: Array<{
    icon: ActivityStatIcon;
    title: string;
    value: string;
  }>;
}) => (
  <>
    <div className="mb-4">
      <TableFilters filters={filters} />
    </div>

    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[19px] w-full mb-8 max-w-[856px]">
      {stats.map(({ icon, title, value }) => (
        <div key={title}>
          <ActivityStat icon={icon} title={title} value={value} />
        </div>
      ))}
    </div>
  </>
);

const ActivityDashboard = () => {
  const { t } = useTranslation();

  return (
    <PageLayout background="muted" title={t('page.activityDashboard.title')} width="wide">
      <Stats />
    </PageLayout>
  );
};

const ActivityDashboardWithAuth = withAuthenticationContext(ActivityDashboard);

export { ActivityDashboardWithAuth as ActivityDashboard };
